import getLocaleHeaderByLocale from '../../../lib/getLocaleHeaderByLocale'
import { bookingClient, getHeaderLocale, spaceClient, ecboClient } from 'lib/axios'
import { i18n } from 'next-i18next'

const API_BOOKING = '/api/app/v1/bookings'
const API_BOOKING_INFO = '/api/app/v1/bookings/info'
const API_BOOKING_DETAIL = '/api/app/v1/bookings/'
const API_CANCEL_BOOKING = '/api/app/v1/bookings/'
const API_FETCH_PAYMENT_INTENTS = '/api/app/v1/bookings/payment_intents'
// new api support PUDO reservation
const API_RESERVATIONS = '/api/web/reservations'
const API_RESERVATION_DETAIL = '/api/web/reservations'
const API_BOOKING_VALIDATE = '/api/web/reservations/validate'
const API_BOOKING_CANCEL = (id: string) => `/api/web/reservations/${id}/cancel`

type FetchPaymentIntentParams = {
  space_id: string
  booked_from: string
  booked_to: string
  small_lockers_count: number
  large_lockers_count: number
}

export const fetchPaymentIntents = async (
  params: FetchPaymentIntentParams,
  accessToken: string
) => {
  try {
    const { space_id, booked_from, booked_to, small_lockers_count, large_lockers_count } = params
    const response = await bookingClient.post(
      API_FETCH_PAYMENT_INTENTS,
      {
        space_id,
        booked_from,
        booked_to,
        small_lockers_count,
        large_lockers_count,
      },
      {
        headers: {
          Authorization: `Token ${accessToken}`,
        },
      }
    )

    return { isSuccess: true, data: response.data }
  } catch (error) {
    return { isSuccess: false }
  }
}

export const postBooking = (accessToken, params) =>
  bookingClient.post(
    API_BOOKING,
    {
      space_id: params.space_id,
      booked_from: params.booked_from,
      booked_to: params.booked_to,
      small_lockers_count: Number(params.bag_size),
      large_lockers_count: Number(params.suitcase_size),
      source: params.source ?? 0,
    },
    {
      headers: {
        Authorization: `Token ${accessToken}`,
      },
    }
  )

export const getBookingInfo = (accessToken, params) =>
  bookingClient.get(API_BOOKING_INFO, {
    params: {
      space_id: params.space_id,
      booked_from: params.booked_from,
      booked_to: params.booked_to,
      small_lockers_count: Number(params.bag_size),
      large_lockers_count: Number(params.suitcase_size),
    },
    headers: {
      Authorization: `Token ${accessToken}`,
    },
  })

export const getBookingDetail = (accessToken, bookingId) =>
  bookingClient.get(API_BOOKING_DETAIL + bookingId, {
    headers: {
      Authorization: `Token ${accessToken}`,
    },
  })

export const cancelBooking = (accessToken, bookingId) =>
  bookingClient.patch(
    API_CANCEL_BOOKING + `${bookingId}/cancel`,
    { encrypted_id: bookingId },
    {
      headers: {
        Authorization: `Token ${accessToken}`,
      },
    }
  )

export const getReservations = async (
  accessToken: string,
  type: 'current' | 'future' | 'past',
  cursor?: string
) => {
  try {
    const response = await ecboClient.get(API_RESERVATIONS, {
      headers: {
        Authorization: `Token ${accessToken}`,
        locale: getLocaleHeaderByLocale(i18n.language || 'ja'),
      },
      params: {
        type,
        cursor,
      },
    })

    const { reservations, has_more } = response.data

    return {
      isOk: true,
      data: reservations,
      has_more,
    }
  } catch (error) {
    return {
      isOk: false,
      data: [],
      has_more: false,
    }
  }
}

export const getReservationDetail = async (reservationId: string, accessToken: string) => {
  try {
    const response = await ecboClient.get(API_RESERVATION_DETAIL + `/${reservationId}`, {
      headers: {
        Authorization: `Token ${accessToken}`,
        locale: getLocaleHeaderByLocale(i18n.language || 'ja'),
      },
    })

    const { reservation } = response.data

    return {
      isOk: true,
      data: reservation,
    }
  } catch (error) {
    return {
      isOk: false,
      data: null,
    }
  }
}

export const getBookings = (
  accessToken: string,
  bookingType: 'current' | 'history',
  page: number = 1
) =>
  bookingClient.get(API_BOOKING_DETAIL, {
    headers: {
      Authorization: `Token ${accessToken}`,
      locale: getHeaderLocale(i18n.language),
    },
    params: {
      booking_type: bookingType,
      page,
      per: bookingType === 'current' ? 100 : 10,
    },
  })

type IGetBookingStatusParams = {
  space_id: string
  start_date: string
  end_date: string
}

export const getBookingStatus: (data: IGetBookingStatusParams) => Promise<any> = async (data) => {
  try {
    const response = await spaceClient.post(`/api/app/v1/spaces/${data.space_id}/bookings_status`, {
      start_date: data.start_date,
      end_date: data.end_date,
    })

    return {
      isOk: true,
      data: response.data,
    }
  } catch (error) {
    return {
      isOk: false,
    }
  }
}

type IGetLockerBookingStatusParams = {
  locker_id: string
  start_date: string
  end_date: string
}

// Define booking status types
export type BookingStatusItem = {
  date: string
  day_of_week: string
  day_of_week_label: string
  ratio: number
  is_holiday: boolean
  // Add other booking properties as needed
}

export type LockerBookingStatusData = {
  locker: {
    bookings: BookingStatusItem[]
    locker_id: string
    // Add other locker properties as needed
  }
}

export type LockerBookingStatusResponse =
  | {
      isOk: true
      data: LockerBookingStatusData
    }
  | {
      isOk: false
      data?: any
    }

export const getLockerBookingStatus: (
  data: IGetLockerBookingStatusParams
) => Promise<LockerBookingStatusResponse> = async (data) => {
  try {
    const response = await bookingClient.post(
      `/api/app/v1/locations/lockers/${data.locker_id}/bookings_status`,
      {
        start_date: data.start_date,
        end_date: data.end_date,
      }
    )

    return {
      isOk: true,
      data: response.data,
    }
  } catch (error) {
    return {
      isOk: false,
    }
  }
}

type ICheckBookingAvailabilityParams = {
  space_id: string
  booked_from: string
  booked_to: string
  small_lockers_count: number
  large_lockers_count: number
}

export const checkBookingAvailability: (
  data: ICheckBookingAvailabilityParams,
  accessToken: string,
  locale: string
) => Promise<any> = async (data, accessToken, locale) => {
  try {
    const response = await bookingClient.get('/api/app/v1/bookings/validate', {
      params: {
        ...data,
      },
      headers: {
        Authorization: `Token ${accessToken}`,
        LOCALE: getLocaleHeaderByLocale(locale || 'ja'),
      },
    })

    return {
      isOk: true,
      data: response.data,
    }
  } catch (error) {
    return {
      isOk: false,
      data: error?.response?.data,
    }
  }
}

export type ValidateBookingParams = {
  space_id: string
  from: string
  to: string
  reservation_items: { [key: string]: number }
}

export const validateBookingReservation = async (
  params: ValidateBookingParams,
  accessToken: string
) => {
  try {
    const response = await ecboClient.post(API_BOOKING_VALIDATE, params, {
      headers: { Authorization: `Token ${accessToken}` },
    })

    return {
      valid: response.data.valid,
    }
  } catch (error) {
    return {
      valid: false,
      errors: error?.response?.data?.errors || null,
    }
  }
}

export type CancelReservationParams = {
  reservationId: string
  isSuspend?: boolean
}

export const cancelReservation = async (params: CancelReservationParams, accessToken: string) => {
  try {
    await ecboClient.post(
      API_BOOKING_CANCEL(params.reservationId),
      {
        is_suspend: params.isSuspend,
      },
      {
        headers: { Authorization: `Token ${accessToken}` },
      }
    )

    return {
      isOk: true,
    }
  } catch (error) {
    return {
      isOk: false,
    }
  }
}

export type ReservationRegisterParams = {
  space_id: string
  from: string
  to: string
  reservation_items: { [key: string]: number }
  wallet_pay?: boolean
}

export const registerReservation = async (
  params: ReservationRegisterParams,
  accessToken: string
) => {
  try {
    const response = await ecboClient.post(
      API_RESERVATIONS,
      { ...params, payment_method: 0, source: 'browser' },
      {
        headers: { Authorization: `Token ${accessToken}` },
      }
    )

    return {
      isOk: true,
      data: response.data.reservation,
    }
  } catch (error) {
    const { errors } = error.response.data

    let errorMessage = ''

    if (errors.length) {
      // TODO: need to comfirm response of errors
    } else {
      Object.keys(errors).forEach((errorKey) => {
        errorMessage += errors[errorKey][0] + '\n'
      })
    }

    return {
      isOk: false,
      error: errorMessage,
    }
  }
}
